import type { IconName } from '@/components/ui/Icons'

/**
 * Content that appears on more than one route. Defined once so the home page
 * summary and the dedicated page can never drift apart.
 *
 * Every string here is the product's own copy, carried over from the approved
 * build. Nothing in this file asserts a customer, a metric or a claim that the
 * site did not already make.
 */

export interface PipelineStage {
  id: string
  name: string
  detail: string
  icon: IconName
}

/** The eight stages of the data plane — home (section 04) and /method. */
export const PIPELINE_STAGES: PipelineStage[] = [
  {
    id: '01',
    name: 'Land',
    detail: 'Raw bytes stored unmodified, before anything reads them.',
    icon: 'land',
  },
  {
    id: '02',
    name: 'Parse',
    icon: 'parse',
    detail: 'Full-fidelity read. Every sheet, every row, text cells kept.',
  },
  {
    id: '03',
    name: 'Discover',
    icon: 'discover',
    detail: 'Where the table actually starts. Header row seven is normal.',
  },
  {
    id: '04',
    name: 'Profile',
    icon: 'profile',
    detail: 'What each column is, per column — not one array shared by everyone.',
  },
  {
    id: '05',
    name: 'Calendar',
    icon: 'calendar',
    detail: 'Your fiscal year, your quarter ends, your period labels.',
  },
  {
    id: '06',
    name: 'Normalize',
    icon: 'normalize',
    detail: 'Units, signs, scale factors. "Figures in ₹ lakh" buried in a title cell counts.',
  },
  {
    id: '07',
    name: 'Reconcile',
    icon: 'reconcile',
    detail: 'Does it add up. Where it does not, the discrepancy is surfaced, not smoothed.',
  },
  {
    id: '08',
    name: 'Assemble',
    icon: 'assemble',
    detail: 'Measures become panels. One snapshot, one shape, one schema version.',
  },
]

export interface ProductSection {
  group: 'Company' | 'Core' | 'Delivery' | 'Governance'
  name: string
  slug: string
  detail: string
  icon: IconName
}

/** The seven sections — home (section 03) and /sections. */
export const PRODUCT_SECTIONS: ProductSection[] = [
  {
    group: 'Company',
    name: 'Overview',
    icon: 'overview',
    slug: 'overview',
    detail: 'The handful of figures a founder checks first, and the signals ranked against them.',
  },
  {
    group: 'Core',
    name: 'Finance',
    icon: 'finance',
    slug: 'finance',
    detail:
      'The safe section: a trial balance alone supports it. Revenue, margin, cash, working capital.',
  },
  {
    group: 'Core',
    name: 'Sales',
    icon: 'sales',
    slug: 'sales',
    detail: 'Pipeline and conversion, which need a CRM export rather than a ledger.',
  },
  {
    group: 'Core',
    name: 'Marketing & Growth',
    icon: 'marketing',
    slug: 'marketing',
    detail: 'Spend is in the ledger. Attribution is not, and we do not pretend otherwise.',
  },
  {
    group: 'Delivery',
    name: 'Operations',
    icon: 'operations',
    slug: 'operations',
    detail: 'Utilisation, rework. Capacity is a fact about plant and shifts; no export states it.',
  },
  {
    group: 'Delivery',
    name: 'Supply chain',
    icon: 'supply',
    slug: 'supply',
    detail:
      'Inventory cover computes today. Vendor concentration is the closest locked panel in the product.',
  },
  {
    group: 'Governance',
    name: 'Compliance',
    icon: 'compliance',
    slug: 'compliance',
    detail: 'Calendar arithmetic over due dates and filed dates. Both are document facts.',
  },
]

export interface ForecastTier {
  id: string
  name: string
  detail: string
  icon: IconName
}

/** The forecast ladder — home (section 06) and /projection. */
export const FORECAST_TIERS: ForecastTier[] = [
  {
    id: 'T0',
    name: 'Fixture',
    detail: 'The design reference path. Never a customer number.',
    icon: 'fixture',
  },
  {
    id: 'T1',
    name: 'TimesFM',
    icon: 'model',
    detail: 'Foundation model, native quantiles, only when the real decoder answered.',
  },
  {
    id: 'T2',
    name: 'TimesFM with regressors',
    icon: 'model-plus',
    detail: 'Same, with exogenous series the data plane could establish.',
  },
  {
    id: 'T3',
    name: 'Classical',
    icon: 'classical',
    detail: 'Where the ladder starts when weights are not installed.',
  },
  {
    id: 'T4',
    name: 'Bootstrap',
    icon: 'bootstrap',
    detail: 'Empirical paths, when a parametric fit is not defensible.',
  },
  {
    id: 'T5',
    name: 'Locked',
    icon: 'locked',
    detail: 'Not enough history to project. The panel says so and shows nothing.',
  },
]

export interface TraceStage {
  kind: 'Recorded' | 'Computed' | 'Projected'
  name: string
  detail: string
  icon: IconName
}

/** The five trace stages — home (section 07) and /provenance. */
export const TRACE_STAGES: TraceStage[] = [
  {
    kind: 'Recorded',
    name: 'Source',
    icon: 'source',
    detail: 'The file, the sheet, the row. What you sent, identified.',
  },
  {
    kind: 'Recorded',
    name: 'Extraction',
    icon: 'extraction',
    detail: 'The cell as written, and the value we read out of it.',
  },
  {
    kind: 'Computed',
    name: 'Calculation',
    icon: 'calculation',
    detail: 'The formula, with each measure substituted.',
  },
  {
    kind: 'Computed',
    name: 'Model check',
    icon: 'check',
    detail: 'Holdout, fit, and whether the history was sufficient at all.',
  },
  {
    kind: 'Projected',
    name: 'Projection',
    icon: 'projection',
    detail: 'Tier, decoder, quantile source, and the band it produced.',
  },
]

export interface CataloguePanel {
  name: string
  id: string
  state: 'computes' | 'locked'
  reason: string
}

/** Selected catalogue panels — home (section 05) and /sections. */
export const CATALOGUE_PANELS: CataloguePanel[] = [
  {
    name: 'Inventory cover',
    id: 'supply.inventory_cover',
    state: 'computes',
    reason: 'Closing inventory over cost of goods sold, in days of the period.',
  },
  {
    name: 'Vendor concentration',
    id: 'supply.vendor_concentration',
    state: 'locked',
    reason:
      'Needs purchases by vendor — a purchase register with a vendor column, or creditor-wise totals.',
  },
  {
    name: 'Lead time',
    id: 'supply.lead_time',
    state: 'locked',
    reason: 'Order date to receipt date. Neither is in a ledger.',
  },
  {
    name: 'Cost variance to plan',
    id: 'supply.cost_variance_to_plan',
    state: 'locked',
    reason: 'A variance needs a plan. The ledger records what was spent, never what was budgeted.',
  },
  {
    name: 'Utilisation',
    id: 'operations.utilisation',
    state: 'locked',
    reason:
      'Output against available capacity. Capacity is a fact about plant and shifts; no accounting export states it.',
  },
  {
    name: 'Filings on time',
    id: 'compliance.filings_on_time',
    state: 'locked',
    reason:
      'Calendar arithmetic over due dates and filed dates. Unlocked by a filing calendar or GST acknowledgements.',
  },
]

export interface ClaimTag {
  tag: string
  meaning: string
}

/** How the company tags its own claims — home (section 09) and /company. */
export const CLAIM_TAGS: ClaimTag[] = [
  { tag: '[repo:path:L120]', meaning: 'Read in the source. Strongest.' },
  { tag: '[doc:NAME.md]', meaning: 'Asserted in a repository document.' },
  { tag: '[founder]', meaning: 'Founder-attested. Real, but not independently verifiable here.' },
  { tag: '[inferred]', meaning: 'Reasoned from other facts. Must show the reasoning.' },
  { tag: '[TBD-founder]', meaning: 'Unknown, and recorded as unknown rather than filled in.' },
]

export interface Accreditation {
  name: string
  detail: string
  tag: string
}

/** Third-party validation — home (section 10) and /company. */
export const ACCREDITATIONS: Accreditation[] = [
  { name: 'NVIDIA Inception', detail: 'Accepted. Documentation available.', tag: '[founder]' },
  {
    name: 'Microsoft for Startups',
    detail: 'Founders Hub. Accepted. Documentation available.',
    tag: '[founder]',
  },
  {
    name: 'Anthropic startup programme',
    detail: 'Accepted. Documentation available.',
    tag: '[founder]',
  },
]

export interface ReadinessItem {
  label: string
  state: string
  done: boolean
}

/** Phase 0 readiness — home (section 11). */
export const READINESS: ReadinessItem[] = [
  { label: 'Repository boots, gates blocking', state: 'done', done: true },
  { label: 'Engine capabilities importable', state: '8 of 8', done: true },
  { label: 'Dashboard', state: 'not built', done: false },
  { label: 'Setup wizard', state: 'not built', done: false },
  { label: 'Projections', state: 'flag off by default', done: false },
  { label: 'Ask bar', state: 'flag off by default', done: false },
  { label: 'Connectors', state: 'not built', done: false },
]

export interface ExportShape {
  sample: string
  name: string
  detail: string
  icon: IconName
}

/** What real exports look like — home (section 08) and /method. */
export const EXPORT_SHAPES: ExportShape[] = [
  {
    sample: 'A1:G6 → junk',
    name: 'Header on row 7',
    icon: 'header-row',
    detail: 'Six rows of company name, address and report title above the actual column headers.',
  },
  {
    sample: '(1,234)',
    name: 'Bracketed negatives',
    icon: 'brackets',
    detail: 'Read as text by anything that guesses dtypes, which silently loses the sign.',
  },
  {
    sample: '1,23,456',
    name: 'Indian grouping',
    icon: 'grouping',
    detail:
      'Lakh and crore separators. A parser tuned for thousands gets this wrong by orders of magnitude.',
  },
  {
    sample: 'Σ mid-sheet',
    name: 'Subtotals in the data',
    icon: 'sigma',
    detail: 'Group totals interleaved with rows, ready to be double-counted.',
  },
  {
    sample: '₹ lakh',
    name: 'Scale in a title cell',
    icon: 'scale-note',
    detail: '"Figures in ₹ lakh" written once, in prose, three rows above the numbers it governs.',
  },
]
